import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./contexts/AuthContext";
import { useTheme } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminRoute from "./components/auth/AdminRoute";
import Layout from "./components/layout/Layout";
import GlobalLegalFooter from "./components/layout/GlobalLegalFooter";

// Auth components
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import TwoFactorChallenge from "./components/auth/TwoFactorChallenge";
import VerifyEmail from "./pages/VerifyEmail";
import TermsOfUse from "./pages/TermsOfUse";
import PrivacyNotice from "./pages/PrivacyNotice";
import LegalDocument from "./components/legal/LegalDocument";

// Dashboard components
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Tokens from "./pages/Tokens";
import Usage from "./pages/Usage";
import Billing from "./pages/Billing";
import AllUsersBilling from "./pages/AllUsersBilling";
import AdvancedTranscriptionTesting from "./pages/AdvancedTranscriptionTesting";
import AdvancedLLMTestingPage from "./pages/AdvancedLLMTestingPage";
import WERDashboard from "./pages/WERDashboard";
import ApiDocumentation from "./pages/ApiDocumentation";
import InviteManagement from "./pages/InviteManagement";
import AdminTokenManagement from "./pages/AdminTokenManagement";

const ToasterComponent = () => {
  const { isDark } = useTheme();

  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          background: isDark ? "#374151" : "#fff",
          color: isDark ? "#f3f4f6" : "#374151",
          boxShadow: isDark
            ? "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)"
            : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        },
        success: {
          iconTheme: {
            primary: "#10b981",
            secondary: isDark ? "#374151" : "#fff",
          },
        },
        error: {
          iconTheme: {
            primary: "#ef4444",
            secondary: isDark ? "#374151" : "#fff",
          },
        },
      }}
    />
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App app-midnight flex min-h-screen flex-col bg-gradient-to-br from-[#0b1224] via-[#111c2f] to-[#0b1224] text-slate-100">
          <ToasterComponent />

          <div className="flex-1">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/2fa-challenge" element={<TwoFactorChallenge />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/privacy" element={<PrivacyNotice />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="profile" element={<Profile />} />
                <Route path="tokens" element={<Tokens />} />
                <Route path="usage" element={<Usage />} />
                <Route path="api-docs" element={<ApiDocumentation />} />
                <Route path="billing" element={<Billing />} />
                <Route
                  path="legal/terms"
                  element={<LegalDocument type="terms" embedded />}
                />
                <Route
                  path="legal/privacy"
                  element={<LegalDocument type="privacy" embedded />}
                />
                <Route
                  path="admin/all-users-billing"
                  element={
                    <AdminRoute>
                      <AllUsersBilling />
                    </AdminRoute>
                  }
                />
                <Route
                  path="transcription-testing"
                  element={
                    <AdminRoute>
                      <AdvancedTranscriptionTesting />
                    </AdminRoute>
                  }
                />
                <Route
                  path="llm-testing"
                  element={
                    <AdminRoute>
                      <AdvancedLLMTestingPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="wer-dashboard"
                  element={
                    <AdminRoute>
                      <WERDashboard />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/invites"
                  element={
                    <AdminRoute>
                      <InviteManagement />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/tokens"
                  element={
                    <AdminRoute>
                      <AdminTokenManagement />
                    </AdminRoute>
                  }
                />
              </Route>

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>

          <GlobalLegalFooter />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
